// @ts-nocheck
import React, { useCallback, useMemo, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Tooltip } from '@mui/material';

import { momentTz } from '../../../../utils/dates';
import { ReactComponent as CalendarIcon } from '../../../../assets/Icons/CalendarIcon.svg';
import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';
import { isValueEmpty, getErrorHelperText } from '../../../../utils/Utils';
import DatePicker from '../../DatePicker';
import {
  EMPTY_OBJECT,
  YYYY_MM_DD_DATE_FORMAT,
} from '../../../../const/CommonConst';
import { composeDraftBlurHandler } from '../../../../hooks/useDraftAutoSave';
import { DRAFT_INPUT_SX } from './TextFieldComponent';
import { NEW_VARIANT_ICON_COLOR } from './DraftFieldExtras';

/**
 * Custom picker button component for DatePicker
 * Wraps an IconButton with a Tooltip and custom click handling
 * Supports both regular calendar picker and derived attribute calculation
 * @component
 * @param {Object} props - Component props
 * @param {string} props.tooltipTitle - Text to display in the tooltip
 * @param {Function} props.onPickerClick - Callback when picker button is clicked
 * @param {Function} props.onClick - Additional click handler from MUI DatePicker
 * @param {React.Ref} ref - Forwarded ref to the IconButton
 * @returns {React.ReactElement} Tooltip-wrapped IconButton with custom click handling
 * @example
 * // Used internally by DatePicker for calendar icon
 */
const PickerButton = forwardRef(function PickerButton(
  { tooltipTitle, onPickerClick, ...props },
  ref
) {
  const handleClick = useCallback(
    (e) => {
      onPickerClick?.(e);
      props.onClick?.(e);
    },
    [onPickerClick, props.onClick]
  );

  return (
    <Tooltip title={tooltipTitle}>
      <IconButton ref={ref} {...props} onClick={handleClick} />
    </Tooltip>
  );
});

PickerButton.propTypes = {
  tooltipTitle: PropTypes.node,
  onPickerClick: PropTypes.func,
  onClick: PropTypes.func,
};

/**
 * Date field component for editing date attributes
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {string|Date} props.value - Current field value
 * @param {Function} props.onChange - Function to handle value changes
 * @param {Object} props.attribute - The attribute being edited
 * @param {Object} props.entityDetailsFormikForm - Formik form instance for error handling
 * @returns {React.ReactElement} The DatePicker component
 */

const DateFieldComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  required = false,
  attribute,
  entityDetailsFormikForm,
  handleOpenDerivedModal,
  hideHeader,
  isLoadingConstraints = false,
  dateCalendarConstraints = EMPTY_OBJECT,
  hasDraftChip = false,
  onDraftBlur,
  isNewVariant = false,
}) => {
  const [open, setOpen] = useState(false);
  const isDerived = useMemo(() => {
    return hideHeader
      ? attribute?.is_derived && attribute?.permission === 'manage'
      : attribute?.is_derived;
  }, [hideHeader, attribute?.is_derived, attribute?.permission]);

  /**
   * Handles date change events
   * Converts selected date to YYYY-MM-DD format and calls onChange callback
   * @function
   * @param {Date} date - The selected date from MUI DatePicker
   * @returns {void}
   */
  const handleDateChange = useCallback(
    (date) => {
      if (date) {
        onChange(momentTz(date)?.format(YYYY_MM_DD_DATE_FORMAT));
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  // Parse the input value to a momentTz object if it exists
  const dateValue = useMemo(() => {
    return value ? momentTz(value) : null;
  }, [value]);

  // Extract date calendar constraints for this attribute
  const dateConstraints = useMemo(() => {
    const attributeId = attribute?.id;
    const constraints = dateCalendarConstraints[attributeId];

    if (!constraints) {
      return { minDate: null, maxDate: null };
    }

    return {
      minDate: constraints?.min_date ? momentTz(constraints.min_date) : null,
      maxDate: constraints?.max_date ? momentTz(constraints.max_date) : null,
    };
  }, [dateCalendarConstraints, attribute?.id]);

  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );

  // Opens the date picker calendar Only opens for non-derived attributes
  const handleOpen = useCallback(() => {
    if (!isDerived && !isLoadingConstraints) {
      setOpen(true);
    }
  }, [isDerived, isLoadingConstraints, setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onDraftBlur?.();
  }, [onDraftBlur]);

  const { onBlur: formikBlur, ...fieldProps } =
    entityDetailsFormikForm?.getFieldProps(attribute?.id) || {};

  const handleBlur = useCallback(
    (event) => {
      composeDraftBlurHandler(formikBlur, onDraftBlur)(event);
    },
    [formikBlur, onDraftBlur]
  );

  // Handles click on the date input field Opens the picker if attribute is editable and not derived
  const handleFieldClick = useCallback(() => {
    if (!attribute?.is_editable || isDerived || isLoadingConstraints) {
      return;
    }
    setOpen(true);
  }, [attribute?.is_editable, isDerived, isLoadingConstraints, setOpen]);

  const derivedTooltipTitle = useMemo(() => {
    if (!isDerived) {
      return '';
    }
    return !attribute?.show_calculation_popUp
      ? 'Enable the calculation popup to calculate this attribute.'
      : `Calculate ${attribute?.label}`;
  }, [isDerived, attribute?.show_calculation_popUp, attribute?.label]);

  const slots = useMemo(
    () => ({
      openPickerIcon: isDerived ? CalculationIcon : CalendarIcon,
      openPickerButton: PickerButton,
    }),
    [isDerived]
  );

  const slotProps = useMemo(
    () => ({
      field: {
        clearable: !isValueEmpty(value),
      },
      openPickerButton: {
        disabled: isDerived
          ? !attribute?.show_calculation_popUp
          : isLoadingConstraints,
        tooltipTitle: isDerived ? derivedTooltipTitle : 'Select Date',
        onPickerClick: isDerived ? handleClickAttribute : undefined,
        className: isDerived ? 'disabled-svg-icon' : undefined,
        sx: {
          p: 0,
          mr: 0,
          ...(!isDerived && isNewVariant
            ? { color: NEW_VARIANT_ICON_COLOR }
            : undefined),
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
        },
      },
      textField: {
        onClick: handleFieldClick,
        sx: hasDraftChip ? DRAFT_INPUT_SX : undefined,
      },
    }),
    [
      value,
      isDerived,
      derivedTooltipTitle,
      handleClickAttribute,
      attribute?.show_calculation_popUp,
      handleFieldClick,
      hasDraftChip,
      isNewVariant,
      isLoadingConstraints,
    ]
  );

  return (
    <DatePicker
      {...fieldProps}
      onBlur={handleBlur}
      attribute={attribute}
      entityDetailsFormikForm={entityDetailsFormikForm}
      label={label}
      title={typeof label === 'string' ? titleLabel : undefined}
      required={required}
      value={dateValue}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      minDate={dateConstraints?.minDate}
      maxDate={dateConstraints?.maxDate}
      disabled={!attribute?.is_editable || isDerived}
      readOnly={isLoadingConstraints}
      onChange={handleDateChange}
      error={Boolean(
        getErrorHelperText(
          entityDetailsFormikForm?.errors[attribute?.id],
          entityDetailsFormikForm?.touched[attribute?.id]
        )
      )}
      helperText={getErrorHelperText(
        entityDetailsFormikForm?.errors[attribute?.id],
        entityDetailsFormikForm?.touched[attribute?.id]
      )}
      slots={slots}
      slotProps={slotProps}
    />
  );
};

DateFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    label: PropTypes.string,
    type: PropTypes.string,
    is_editable: PropTypes.bool,
    is_derived: PropTypes.string,
    permission: PropTypes.string,
    show_calculation_popUp: PropTypes.bool,
    // Add more specific properties as needed
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    getFieldProps: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
  }),
  handleOpenDerivedModal: PropTypes.func.isRequired,
  hideHeader: PropTypes.bool.isRequired,
  isLoadingConstraints: PropTypes.bool,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
  isNewVariant: PropTypes.bool,
  dateCalendarConstraints: PropTypes.objectOf(
    PropTypes.shape({
      min_date: PropTypes.string,
      max_date: PropTypes.string,
      format: PropTypes.string,
    })
  ),
};

export default DateFieldComponent;
